// client/src/coms/organisation/ConnectStorageDialog.js
// "Connect storage": your connected accounts (who, status, what Voxemera may do; browse, reconnect, disconnect),
// connecting another account with the provider's own sign-in, and, for storage managers, adding a library to
// the server (including S3, which is configured with a role or bucket-scoped keys, never a console password).
// Providers this Voxemera site has not registered with show "Connection not available yet"; providers whose
// integration is not finished show "Coming Soon" with no sign-in button. Voxemera's own small-file storage is
// shown separately, with its limit and what is left.
import React, { useCallback, useEffect, useState } from 'react';
import { apiGetJson, apiPostJson } from './orgApi';
import { Dialog, ProviderBadge, Spinner, ErrorNote, notify, PROVIDERS, useConfirm, formatBytes } from './orgShared';
import { connectWithFeedback, disconnectAccount, AccountCard } from './storageAccounts';
import AccountBrowser from './AccountBrowser';

const MODEL_TEXT = {
  member_links: {
    title: 'Members link files from their own Drive',
    body: 'Members who may upload files pick files from their own Google Drive. Each link says who in this server can see it, and opening a file always happens in Google Drive, whose sharing decides who can open it. Recommended.',
  },
  personal_accounts: {
    title: 'Each member uses their own account',
    body: 'Members connect their own account to browse. The provider decides exactly what each person can see. Recommended for Google Drive, Dropbox and OneDrive.',
  },
  shared_connection: {
    title: 'Shared through an account you connect for the server',
    body: 'That account lists the files for members with library access. For Drive, Dropbox and OneDrive, opening a file still needs access in that service. Repositories and S3 are served through the connection.',
  },
};

const ACCOUNT_PROVIDERS = ['gdrive', 'dropbox', 'onedrive', 'github', 'gitlab'];

export default function ConnectStorageDialog({ base, serverKey, canManage = false, onClose, onConnected, initialView = 'home' }) {
  const [catalog, setCatalog] = useState(null);
  const [conns, setConns] = useState(null);
  const [err, setErr] = useState(null);
  const [view, setView] = useState(initialView);
  const [browse, setBrowse] = useState(null);
  const [libProvider, setLibProvider] = useState(null);
  const [confirm, confirmEl] = useConfirm();

  const [native, setNative] = useState(null);
  const loadConns = useCallback(() => apiGetJson('/me/storage/connections').then((r) => setConns(r.connections), setErr), []);
  useEffect(() => {
    apiGetJson('/storage/providers').then(setCatalog, setErr);
    loadConns();
  }, [loadConns]);
  useEffect(() => {
    if (!base) return;
    apiGetJson(`${base}/library`).then((r) => setNative(r.storage), () => setNative(null));
  }, [base]);

  async function connect(p, mode) {
    const r = await connectWithFeedback(p.id, p.label, { mode });
    if (r.ok) loadConns();
  }
  async function reconnect(c) {
    const r = await connectWithFeedback(c.provider, c.provider_label, { reconnect: c.id });
    if (r.ok) loadConns();
    return r;
  }
  async function disconnect(c) {
    const ok = await confirm({
      title: `Disconnect ${c.account_label || c.provider_label}?`,
      body: `Voxemera deletes its stored access${c.manage_url ? ` and asks ${c.provider_label} to revoke it` : ''}. Your files in ${c.provider_label} are not touched. Libraries that use this account will ask you to connect again.`,
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (!ok) return;
    try {
      await disconnectAccount(c);
      loadConns();
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }

  if (view === 'browse' && browse) {
    return (
      <Dialog title={`${browse.provider_label}: ${browse.account_label || ''}`} onClose={onClose} wide>
        <AccountBrowser conn={browse} onBack={() => setView('home')} onReconnect={async (c) => (await reconnect(c)).ok && setView('home')} />
      </Dialog>
    );
  }

  if (view === 'library' && catalog) {
    return (
      <LibraryForm
        base={base}
        serverKey={serverKey}
        catalog={catalog}
        initialProvider={libProvider}
        onBack={() => setView('home')}
        onClose={onClose}
        onConnected={onConnected}
      />
    );
  }

  const providers = catalog ? catalog.providers.filter((p) => ACCOUNT_PROVIDERS.includes(p.id)) : [];
  const mine = conns || [];

  return (
    <Dialog title="Connect storage" onClose={onClose} wide>
      <ErrorNote error={err} />
      {!catalog && !err ? <Spinner /> : null}
      {catalog && !catalog.encryption_available ? <div className="vxo-warn">This Voxemera site cannot store connections securely yet, so accounts cannot be connected. The people who run it need to finish its setup.</div> : null}
      {catalog ? (
        <div className="vxo-connect" data-testid="connect-storage-dialog">
          <p className="vxo-hint">You sign in on the provider’s own page; Voxemera never sees your password. Files stay with the provider, and disconnecting never deletes them.</p>
          {mine.length ? (
            <section>
              <h3 className="vxo-h3">Your accounts</h3>
              <div className="vxo-account-list">
                {mine.map((c) => (
                  <AccountCard
                    key={c.id}
                    conn={c}
                    onBrowse={(x) => {
                      setBrowse(x);
                      setView('browse');
                    }}
                    onReconnect={reconnect}
                    onDisconnect={disconnect}
                  />
                ))}
              </div>
            </section>
          ) : null}
          <section>
            <h3 className="vxo-h3">{mine.length ? 'Connect another account' : 'Connect an account'}</h3>
            <div className="vxo-provider-grid">
              {providers.map((p) => {
                const have = mine.filter((c) => c.provider === p.id).length;
                const ready = p.available && p.sign_in;
                if (p.coming_soon) {
                  return (
                    <div key={p.id} className="vxo-provider-card is-off is-soon" data-testid={`provider-${p.id}`} aria-disabled="true">
                      <span className="vxo-provider-card-head">
                        <ProviderBadge provider={p.id} withLabel />
                        <span className="vxo-pill is-off" data-testid={`soon-${p.id}`}>
                          Coming Soon
                        </span>
                      </span>
                      <span className="vxo-muted">{p.label} isn’t available in Voxemera yet.</span>
                    </div>
                  );
                }
                return (
                  <div key={p.id} className={`vxo-provider-card${ready ? '' : ' is-off'}`} data-testid={`provider-${p.id}`}>
                    <span className="vxo-provider-card-head">
                      <ProviderBadge provider={p.id} withLabel />
                      {have ? <span className="vxo-pill is-ok">{have === 1 ? 'Connected' : `${have} connected`}</span> : null}
                    </span>
                    {p.notes ? <span className="vxo-muted">{p.notes}</span> : null}
                    {ready ? (
                      <span className="vxo-actions">
                        <button type="button" className="vx-btn small primary" onClick={() => connect(p, 'read')} data-testid={`connect-${p.id}`}>
                          {have ? 'Connect another account' : 'Connect'}
                        </button>
                        {p.capabilities_write.upload || p.capabilities_write.directUpload ? (
                          <button type="button" className="vx-btn small" onClick={() => connect(p, 'readwrite')} title="Also allow uploads and changes through Voxemera">
                            Connect with uploads
                          </button>
                        ) : null}
                      </span>
                    ) : (
                      <span className="vxo-unavailable" data-testid={`unavailable-${p.id}`}>
                        <span className="vxo-pill is-off">{p.id === 'github' && p.available ? 'Sign-in not available yet' : 'Connection not available yet'}</span>
                        <span className="vxo-muted">
                          {canManage
                            ? p.admin_note || 'The operator of this Voxemera site has not registered Voxemera with GitHub yet. Public repositories can still be added as server libraries.'
                            : 'The people who run this Voxemera site haven’t set this up yet.'}
                        </span>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          {canManage && base ? (
            <section>
              <h3 className="vxo-h3">Add storage to this server</h3>
              <p className="vxo-muted">Show a cloud folder, repository or bucket in this server’s Files for members with library access.</p>
              <div className="vxo-actions">
                {catalog.providers
                  .filter((p) => p.available || p.coming_soon)
                  .map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="vx-btn small"
                      disabled={p.coming_soon}
                      title={p.coming_soon ? `${p.label}: Coming Soon` : undefined}
                      onClick={() => {
                        setLibProvider(p.id);
                        setView('library');
                      }}
                      data-testid={`add-library-${p.id}`}
                    >
                      <ProviderBadge provider={p.id} /> {p.id === 's3' ? 'S3-compatible bucket (advanced)' : p.label}
                      {p.coming_soon ? ' — Coming Soon' : ''}
                    </button>
                  ))}
              </div>
            </section>
          ) : null}
          {native ? (
            <section data-testid="native-storage">
              <h3 className="vxo-h3">Voxemera storage (this server)</h3>
              <p className="vxo-muted">
                Small files uploaded to this server’s Files are stored by Voxemera itself, separately from connected storage: {formatBytes(native.used)} used of {formatBytes(native.quota)}, {formatBytes(native.remaining)} left, files up to {formatBytes(native.max_file)} each. Larger files belong in Google Drive, linked here.
              </p>
            </section>
          ) : null}
        </div>
      ) : null}
      {confirmEl}
    </Dialog>
  );
}

// ------------------------------------------------------------------ add a library (storage managers)
function LibraryForm({ base, serverKey, catalog, initialProvider, onBack, onClose, onConnected }) {
  const provider = initialProvider;
  const p = catalog.providers.find((x) => x.id === provider);
  const [form, setForm] = useState({ mode: 'read', access_model: p ? p.access_models[0] : '', name: p ? (PROVIDERS[provider] || {}).label || p.label : '', root_ref: '', config: {}, token: '', keys: {} });
  const [serverConns, setServerConns] = useState([]);
  const [connId, setConnId] = useState('');
  const [repos, setRepos] = useState(null);
  const [s3Role, setS3Role] = useState(null);
  const [s3Method, setS3Method] = useState('keys');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setCfg = (patch) => setForm((f) => ({ ...f, config: { ...f.config, ...patch } }));
  const model = form.access_model;
  const shared = model === 'shared_connection';
  const git = provider === 'github' || provider === 'gitlab';

  const loadServerConns = useCallback(
    () =>
      apiGetJson(`${base}/library/sources`).then((r) => {
        const list = (r.server_connections || []).filter((c) => c.provider === provider && c.status !== 'revoked');
        setServerConns(list);
        return list;
      }),
    [base, provider]
  );
  useEffect(() => {
    loadServerConns().catch(() => {});
    if (provider === 's3') apiGetJson(`${base}/storage/s3-role`).then((r) => {
      setS3Role(r);
      if (r.available) setS3Method('role');
    }, () => setS3Role({ available: false }));
  }, [base, provider, loadServerConns]);

  useEffect(() => {
    setRepos(null);
    if (!git || !connId) return;
    apiGetJson(`${base}/storage/connections/${connId}/repos`).then(setRepos, (e) => setRepos({ error: e }));
  }, [base, git, connId]);

  async function signInForServer() {
    const r = await connectWithFeedback(provider, p.label, { purpose: 'server', server: serverKey, mode: form.mode });
    if (r.ok) {
      await loadServerConns();
      setConnId(String(r.connection_id));
    }
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const body = { provider, name: form.name || p.label, access_model: model, mode: form.mode, root_ref: form.root_ref || null, root_label: provider === 'dropbox' ? form.root_ref || null : null, config: form.config };
      if (shared && p.auth === 'oauth') {
        if (!connId) throw new Error(`Choose or sign in to a ${p.label} account for this server first`);
        body.connection_id = Number(connId);
      }
      if (git && shared) {
        if (connId) body.connection_id = Number(connId);
        else if (form.token) body.token = form.token;
      }
      if (provider === 's3') {
        body.keys =
          s3Method === 'role'
            ? { role_arn: form.keys.role_arn }
            : {
                access_key_id: form.keys.access_key_id,
                secret_access_key: form.keys.secret_access_key,
                ...(form.keys.session_token ? { session_token: form.keys.session_token, expires_at: form.keys.expires_at ? new Date(form.keys.expires_at).toISOString() : undefined } : {}),
              };
      }
      const out = await apiPostJson(`${base}/library/sources`, body);
      notify(`Added ${out.source.name} to this server`, { tone: 'success' });
      onConnected(out.source);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }

  if (!p) return null;
  return (
    <Dialog
      title={provider === 's3' ? 'Add an S3-compatible bucket' : `Add ${p.label} to this server`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onBack}>
            Back
          </button>
          <button type="button" className="vx-btn primary" disabled={busy} onClick={submit} data-testid="connect-storage-submit">
            {busy ? 'Adding…' : 'Add library'}
          </button>
        </>
      }
    >
      <ErrorNote error={err} />
      <div className="vxo-form">
        <label className="vxo-field">
          <span>Library name</span>
          <input value={form.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
        </label>
        {p.access_models.length > 1 ? (
          <fieldset className="vxo-fieldset">
            <legend>Whose account?</legend>
            {p.access_models.map((m) => (
              <label key={m} className="vxo-radio">
                <input type="radio" name="access_model" checked={model === m} onChange={() => set({ access_model: m })} />
                <span>
                  <strong>{MODEL_TEXT[m].title}</strong>
                  <span className="vxo-muted">{MODEL_TEXT[m].body}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : (
          <p className="vxo-hint">{MODEL_TEXT[p.access_models[0]].body}</p>
        )}
        {model !== 'member_links' ? (
        <fieldset className="vxo-fieldset">
          <legend>Access</legend>
          <label className="vxo-radio">
            <input type="radio" name="mode" checked={form.mode === 'read'} onChange={() => set({ mode: 'read' })} />
            <span>
              <strong>Read-only</strong> <span className="vxo-muted">(recommended) browse, search, open and download</span>
            </span>
          </label>
          <label className="vxo-radio">
            <input type="radio" name="mode" checked={form.mode === 'readwrite'} onChange={() => set({ mode: 'readwrite' })} />
            <span>
              <strong>Read and write</strong> <span className="vxo-muted">uploads and changes through Voxemera, within what the account itself may do</span>
            </span>
          </label>
        </fieldset>
        ) : null}

        {shared && p.auth === 'oauth' ? (
          <AccountPicker p={p} conns={serverConns} value={connId} onChange={setConnId} onSignIn={signInForServer} />
        ) : null}
        {git && shared && p.sign_in ? <AccountPicker p={p} conns={serverConns} value={connId} onChange={setConnId} onSignIn={signInForServer} optional /> : null}

        {provider === 'gdrive' ? (
          <p className="vxo-hint">
            Voxemera only sees the Drive files people pick with Google’s file picker or create through Voxemera (in a “Voxemera” folder), never the rest of anyone’s Drive. Voxemera stores only names and links, never the files. Open the library to add files.
          </p>
        ) : null}
        {provider === 'dropbox' ? (
          <label className="vxo-field">
            <span>Folder path (optional; empty = the whole account)</span>
            <input value={form.root_ref} onChange={(e) => set({ root_ref: e.target.value.trim() })} placeholder="/Community" />
          </label>
        ) : null}
        {provider === 'onedrive' ? (
          <details className="vxo-details-adv">
            <summary>Advanced: SharePoint library or a specific folder</summary>
            <label className="vxo-field">
              <span>SharePoint document library (drive ID; only if this Voxemera site allows SharePoint)</span>
              <input value={form.config.drive_id || ''} onChange={(e) => setCfg({ drive_id: e.target.value.trim() })} />
            </label>
            <label className="vxo-field">
              <span>Folder item ID</span>
              <input value={form.root_ref} onChange={(e) => set({ root_ref: e.target.value.trim() })} />
            </label>
          </details>
        ) : null}

        {git ? (
          <>
            {repos && Array.isArray(repos.repos) && repos.repos.length ? (
              <label className="vxo-field">
                <span>Repository</span>
                <select
                  value={provider === 'github' ? `${form.config.owner || ''}/${form.config.repo || ''}` : form.config.project || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (provider === 'github') {
                      const [owner, repo] = v.split('/');
                      setCfg({ owner, repo });
                    } else setCfg({ project: v, host: 'gitlab.com' });
                  }}
                >
                  <option value="">Choose…</option>
                  {repos.repos.map((r) => (
                    <option key={r.full_name} value={r.full_name}>
                      {r.full_name}
                      {r.private ? ' (private)' : ''}
                    </option>
                  ))}
                </select>
              </label>
            ) : provider === 'github' ? (
              <div className="vxo-row2">
                <label className="vxo-field">
                  <span>Owner</span>
                  <input value={form.config.owner || ''} onChange={(e) => setCfg({ owner: e.target.value.trim() })} placeholder="my-org" />
                </label>
                <label className="vxo-field">
                  <span>Repository</span>
                  <input value={form.config.repo || ''} onChange={(e) => setCfg({ repo: e.target.value.trim() })} placeholder="mods" />
                </label>
              </div>
            ) : (
              <label className="vxo-field">
                <span>Project path</span>
                <input value={form.config.project || ''} onChange={(e) => setCfg({ project: e.target.value.trim() })} placeholder="group/project" />
              </label>
            )}
            {repos && repos.install_url ? (
              <p className="vxo-hint">
                Missing a repository?{' '}
                <a href={repos.install_url} target="_blank" rel="noopener noreferrer">
                  Choose repositories for the Voxemera app on GitHub
                </a>
                , then reopen this form.
              </p>
            ) : null}
            <details className="vxo-details-adv">
              <summary>Branch, subfolder{connId ? '' : ' and access token'}</summary>
              <div className="vxo-row2">
                <label className="vxo-field">
                  <span>Branch or tag (empty = default branch)</span>
                  <input value={form.config.ref || ''} onChange={(e) => setCfg({ ref: e.target.value.trim() })} />
                </label>
                <label className="vxo-field">
                  <span>Subfolder</span>
                  <input value={form.config.subdir || ''} onChange={(e) => setCfg({ subdir: e.target.value.trim() })} />
                </label>
              </div>
              {provider === 'gitlab' && !connId ? (
                <label className="vxo-field">
                  <span>Host (self-managed GitLab only if allowed by this Voxemera site)</span>
                  <input value={form.config.host || 'gitlab.com'} onChange={(e) => setCfg({ host: e.target.value.trim() })} />
                </label>
              ) : null}
              {!connId ? (
                <>
                  <label className="vxo-field">
                    <span>Access token (only for private repositories without sign-in)</span>
                    <input type="password" autoComplete="off" value={form.token} onChange={(e) => set({ token: e.target.value })} placeholder={provider === 'github' ? 'Fine-grained token: this repository only, Contents read' : 'Project access token with read_repository'} />
                  </label>
                  <p className="vxo-hint">Public repositories need no token. A token is stored encrypted and is never shown again.</p>
                </>
              ) : null}
            </details>
          </>
        ) : null}

        {provider === 's3' ? (
          <S3Fields form={form} set={set} setCfg={setCfg} role={s3Role} method={s3Method} setMethod={setS3Method} />
        ) : null}
      </div>
    </Dialog>
  );
}

function AccountPicker({ p, conns, value, onChange, onSignIn, optional = false }) {
  return (
    <fieldset className="vxo-fieldset">
      <legend>{p.label} account for this server{optional ? ' (optional for public repositories)' : ''}</legend>
      {conns.length ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={`${p.label} account`}>
          <option value="">{optional ? 'None (public repository)' : 'Choose an account…'}</option>
          {conns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.account_label || c.provider_label} — {c.status_text}
            </option>
          ))}
        </select>
      ) : null}
      {p.sign_in ? (
        <button type="button" className="vx-btn small" onClick={onSignIn} data-testid="server-sign-in">
          Sign in to {p.label} for this server
        </button>
      ) : (
        <p className="vxo-muted">Connection not available yet: the operator of this Voxemera site has not registered Voxemera with {p.label}.</p>
      )}
    </fieldset>
  );
}

function S3Fields({ form, set, setCfg, role, method, setMethod }) {
  const setKey = (patch) => set({ keys: { ...form.keys, ...patch } });
  return (
    <>
      <p className="vxo-hint">For server administrators. Voxemera never asks for a cloud-console password or an account’s root keys. Use a role Voxemera may assume, or keys limited to this bucket and prefix.</p>
      <label className="vxo-field">
        <span>Endpoint (HTTPS)</span>
        <input value={form.config.endpoint || ''} onChange={(e) => setCfg({ endpoint: e.target.value.trim() })} placeholder="https://s3.eu-west-1.amazonaws.com" />
      </label>
      <div className="vxo-row2">
        <label className="vxo-field">
          <span>Bucket</span>
          <input value={form.config.bucket || ''} onChange={(e) => setCfg({ bucket: e.target.value.trim() })} />
        </label>
        <label className="vxo-field">
          <span>Region</span>
          <input value={form.config.region || ''} onChange={(e) => setCfg({ region: e.target.value.trim() })} placeholder="us-east-1" />
        </label>
      </div>
      <label className="vxo-field">
        <span>Prefix (the library cannot see outside it)</span>
        <input value={form.config.prefix || ''} onChange={(e) => setCfg({ prefix: e.target.value.trim() })} placeholder="community/" />
      </label>
      <label className="vxo-check">
        <input type="checkbox" checked={!!form.config.path_style} onChange={(e) => setCfg({ path_style: e.target.checked })} />
        <span>Path-style URLs (MinIO and some self-hosted servers)</span>
      </label>
      <fieldset className="vxo-fieldset">
        <legend>Credentials</legend>
        {role && role.available ? (
          <label className="vxo-radio">
            <input type="radio" name="s3method" checked={method === 'role'} onChange={() => setMethod('role')} />
            <span>
              <strong>An IAM role Voxemera may assume</strong> <span className="vxo-muted">(recommended on AWS: temporary credentials only, no keys stored)</span>
            </span>
          </label>
        ) : null}
        <label className="vxo-radio">
          <input type="radio" name="s3method" checked={method === 'keys'} onChange={() => setMethod('keys')} />
          <span>
            <strong>Access keys limited to this bucket</strong> <span className="vxo-muted">(any S3-compatible service)</span>
          </span>
        </label>
      </fieldset>
      {method === 'role' && role && role.available ? (
        <>
          <p className="vxo-hint">
            In AWS IAM, create a role that trusts {role.principal_arn ? <code>{role.principal_arn}</code> : 'Voxemera’s federation principal'} with external ID <code data-testid="s3-external-id">{role.external_id}</code>, and give it access to this bucket. Voxemera further limits every session to the prefix above.
          </p>
          {role.trust_policy ? (
            <details className="vxo-details-adv">
              <summary>Trust policy to paste into the role</summary>
              <pre className="vxo-pre">{role.trust_policy}</pre>
            </details>
          ) : null}
          <label className="vxo-field">
            <span>Role ARN</span>
            <input value={form.keys.role_arn || ''} onChange={(e) => setKey({ role_arn: e.target.value.trim() })} placeholder="arn:aws:iam::123456789012:role/voxemera-files" />
          </label>
        </>
      ) : (
        <>
          <div className="vxo-row2">
            <label className="vxo-field">
              <span>Access key ID</span>
              <input autoComplete="off" value={form.keys.access_key_id || ''} onChange={(e) => setKey({ access_key_id: e.target.value.trim() })} />
            </label>
            <label className="vxo-field">
              <span>Secret access key</span>
              <input type="password" autoComplete="off" value={form.keys.secret_access_key || ''} onChange={(e) => setKey({ secret_access_key: e.target.value.trim() })} />
            </label>
          </div>
          <details className="vxo-details-adv">
            <summary>Temporary keys (session token)</summary>
            <label className="vxo-field">
              <span>Session token</span>
              <input type="password" autoComplete="off" value={form.keys.session_token || ''} onChange={(e) => setKey({ session_token: e.target.value.trim() })} />
            </label>
            <label className="vxo-field">
              <span>Expires at</span>
              <input type="datetime-local" value={form.keys.expires_at || ''} onChange={(e) => setKey({ expires_at: e.target.value })} />
            </label>
          </details>
          <p className="vxo-hint">Keys stay on the Voxemera server, encrypted; browsers only receive short-lived signed links. Direct uploads need CORS on the bucket for this site.</p>
        </>
      )}
    </>
  );
}
